import { schema } from 'normalizr';
import * as partnerRoles from 'constants/partnerRoles';

const userRightsSchema = new schema.Entity('userRights', {}, { idAttribute: right => right.authorizationId });

const reportFilterTypeSchema = new schema.Entity('reportFilterTypes', {}, { idAttribute: type => type.filterTypeId });

const reportFiltersSchema = new schema.Entity(
  'reportFilters',
  {
    filterType: reportFilterTypeSchema
  },
  {
    idAttribute: filter => filter.reportFilterId,
    processStrategy: entity => {
      let dynamicInputControlType = '';

      const hasMultipleSelection = entity.allowMultipleSelection ?? false;

      const defaultValue = hasMultipleSelection ? new Array() : entity.defaultValue;
      let value = defaultValue;
      switch (entity.filterTypeCode) {
        case 'Date':
          dynamicInputControlType = 'DATE';
          value = null;
          break;
        case 'Text':
          dynamicInputControlType = 'TP_TEXT';
          break;
        case 'Integer':
          dynamicInputControlType = 'TP_NUM';
          break;
        case 'Amount':
          dynamicInputControlType = 'AMOUNT';
          break;
        case 'Boolean':
          dynamicInputControlType = 'BOOL';
          value = JSON.parse(entity.defaultValue.toLowerCase());
          break;
        case 'Browser':
          dynamicInputControlType = 'BROWSER';
          break;
        case 'Lookup':
          dynamicInputControlType = 'ADAPTIVE_LOOKUP';
          break;
        default:
          break;
      }

      let result = {
        ...entity,
        filterType: { filterTypeId: entity.filterTypeId, code: entity.filterTypeCode, dynamicInputControlType },
        value
      };
      delete result.filterTypeId;
      delete result.filterTypeCode;

      return result;
    }
  }
);

const reportSchema = new schema.Entity(
  'reports',
  {
    filters: [reportFiltersSchema]
  },
  { idAttribute: report => report.reportId }
);

const browserFiltersSchema = new schema.Entity(
  'browserFilters',
  {},
  {
    idAttribute: browserFilter => browserFilter.name,
    processStrategy: (entity, _parent, _key) => {
      let dynamicInputControlType = '';
      switch (entity.filterType) {
        case 'FilterTemplateDate':
          dynamicInputControlType = 'DATE';
          break;
        case 'FilterTemplateText':
          dynamicInputControlType = 'TP_TEXT';
          break;
        case 'FilterTemplateCheck':
          dynamicInputControlType = 'BOOL';
          break;
        case 'AdaptiveLookupFilterTemplate':
          dynamicInputControlType = 'ADAPTIVE_LOOKUP';
          break;
        default:
          break;
      }

      let result = {
        ...entity,
        filterType: { dynamicInputControlType }
      };

      return result;
    }
  }
);

const browserFieldsSchema = new schema.Entity(
  'browserFields',
  {},
  {
    idAttribute: browserField => browserField.name
  }
);

const browserSchema = new schema.Entity(
  'browsers',
  {
    filters: [browserFiltersSchema],
    fields: [browserFieldsSchema]
  },
  { idAttribute: browser => browser.browserName }
);

// const browserDataItemSchema = new schema.Entity('browserDataItems', {}, { idAttribute: entity => entity.id });

const getBrowserDataItemSchema = pkFieldName => {
  const browserDataItemSchema = new schema.Entity('browserDataItems', {}, { idAttribute: entity => entity[pkFieldName] });
  return browserDataItemSchema;
};

const getBrowserDataItems = pkFieldName => {
  return [getBrowserDataItemSchema(pkFieldName)];
};

const financedOffer = new schema.Entity(
  'financedOffers',
  {},
  {
    idAttribute: item => item.offerId
  }
);

const getFolderOfferPartnerIdAttribute = folderOfferPartner => {
  switch (folderOfferPartner.partnerRole) {
    case partnerRoles.CLIENT:
      return `${folderOfferPartner.partnerId}-client`;
    case partnerRoles.GUARANTOR:
      return `${folderOfferPartner.partnerId}-guarantor`;
    case partnerRoles.SHAREHOLDER:
      return `${folderOfferPartner.partnerId}-${folderOfferPartner.shareholderLevel}-shareholder`;
    case partnerRoles.ADMINISTRATOR:
      return `${folderOfferPartner.partnerId}-administrator`;
    default:
      return `${folderOfferPartner.partnerId}`;
  }
};

const offerPartner = new schema.Entity(
  'offerPartners',
  {},
  {
    idAttribute: item => getFolderOfferPartnerIdAttribute(item)
  }
);

const contractGeneration = new schema.Entity(
  'contractGeneration',
  {},
  {
    idAttribute: item => item.offerId + '_' + item.offerSiteId
  }
);

const clientOffer = new schema.Entity(
  'clientOffers',
  {},
  {
    idAttribute: item => item.offerId
  }
);

const facilitySchema = new schema.Entity(
  'facilities',
  {},
  {
    idAttribute: type => type.documentId
  }
);

const facilityRevolvingTypesSchema = new schema.Entity(
  'facilityRevolvingTypes',
  {},
  {
    idAttribute: type => type.id
  }
);

const facilityFlexibilityRulesSchema = new schema.Entity(
  'facilityFlexibilityRules',
  {},
  {
    idAttribute: type => type.id
  }
);

const facilityCreditLineStatusesSchema = new schema.Entity(
  'facilityCreditLineStatuses',
  {},
  {
    idAttribute: type => type.id
  }
);

const facilityPurposesForApplicationSchema = new schema.Entity(
  'facilityPurposesForApplication',
  {},
  {
    idAttribute: type => type.id
  }
);

const facilityPartnerGroupsSchema = new schema.Entity(
  'facilityPartnerGroups',
  {},
  {
    idAttribute: type => type.dictionaryItemId
  }
);

const facilitySublimitsSchema = new schema.Entity(
  'facilitySublimits',
  {},
  {
    idAttribute: type => type.sublimitId
  }
);

const facilityWarrantiesSchema = new schema.Entity(
  'facilityWarranties',
  {},
  {
    idAttribute: type => type.warrantyId
  }
);

const facilitySublimitFiltersSchema = new schema.Entity(
  'facilitySublimitFilters',
  {},
  {
    idAttribute: type => type.filterId
  }
);

const facilitySublimitFinancingStructureFiltersSchema = new schema.Entity(
  'facilitySublimitFinancingStructureFilters',
  {},
  {
    idAttribute: type => type.filterId
  }
);

const responsibleChangeOfferSchema = new schema.Entity(
  'responsibleChangeOffers',
  {},
  { idAttribute: item => `${item.documentId}-${item.siteId}` }
);

const responsibleChangeFolderSchema = new schema.Entity(
  'responsibleChangeFolders',
  {},
  { idAttribute: item => `${item.documentId}-${item.siteId}` }
);

const genericFilterParamValuesSchema = new schema.Entity(
  'genericFilterParamValues',
  {},
  { idAttribute: item => `${item.paramId}-${item.id}` }
);

const genericFilterParamSchema = new schema.Entity(
  'genericFilterParams',
  {
    genericFilterParamValues: [genericFilterParamValuesSchema]
  },
  { idAttribute: type => type.paramId }
);

const genericFilterOperatorSchema = new schema.Entity('genericFilterOperators', {}, { idAttribute: type => type.operatorId });

const contractForRestructuringSchema = new schema.Entity(
  'contractsForRestructuring',
  {},
  { idAttribute: item => `${item.contractId}-${item.contractSiteId}` }
);

const exp = {
  userRights: [userRightsSchema],
  reports: [reportSchema],
  report: reportSchema,
  filters: [reportFiltersSchema],
  browser: browserSchema,
  getBrowserData: getBrowserDataItems,
  financedOffer: financedOffer,
  offerPartner: offerPartner,
  clientOffer: clientOffer,
  contractGeneration: contractGeneration,
  facilities: [facilitySchema],
  facility: facilitySchema,
  facilityRevolvingTypes: [facilityRevolvingTypesSchema],
  facilityFlexibilityRules: [facilityFlexibilityRulesSchema],
  facilityCreditLineStatuses: [facilityCreditLineStatusesSchema],
  facilityPurposesForApplication: [facilityPurposesForApplicationSchema],
  facilityPartnerGroups: [facilityPartnerGroupsSchema],
  facilityWarranties: [facilityWarrantiesSchema],
  facilitySublimits: [facilitySublimitsSchema],
  facilitySublimitFilters: [facilitySublimitFiltersSchema],
  facilitySublimitFinancingStructureFilters: [facilitySublimitFinancingStructureFiltersSchema],
  responsibleChangeOffers: [responsibleChangeOfferSchema],
  responsibleChangeFolders: [responsibleChangeFolderSchema],
  genericFilterParams: [genericFilterParamSchema],
  genericFilterOperators: [genericFilterOperatorSchema],
  genericFilterParamValues: [genericFilterParamValuesSchema],
  contractsForRestructuring: [contractForRestructuringSchema]
};

export default exp;
